import type { ComponentProps } from 'react';
import React from 'react';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import { theme } from '@garnish/constants';
import { IconLink } from '../../Icon';
import { HStack } from '../../Stack';
import { FadeView } from '../../FadeView';
import { HorizontalScrollRailCount } from './HorizontalScrollRailCount';
import { StyleSheet, View } from 'react-native';

// ────────────────────────────────────────────────────────────────────────────────

export const HorizontalScrollRailNav = (props: HorizontalScrollNavProps) => {
  const {
    count,
    showNavControls = false,
    disablePrevBtn = true,
    disableNextBtn = true,
    prevBtnA11yLabel = 'Previous item',
    nextBtnA11yLabel = 'Next item',
    onPrevBtnPress,
    onNextBtnPress,
  } = props;

  const prevBtnAnimatedStyle = useNavBtnAnimatedStyle({
    disabled: disablePrevBtn,
  });
  const nextBtnAnimatedStyle = useNavBtnAnimatedStyle({
    disabled: disableNextBtn,
  });

  return (
    <View style={styles.rowCenter}>
      <HorizontalScrollRailCount count={count} />
      <FadeView show={showNavControls}>
        <HStack customSpacing={theme.spacing['4']}>
          <HorizontalScrollRailNavBtn
            name="IconArrowLeft"
            onPress={onPrevBtnPress}
            disabled={disablePrevBtn}
            style={prevBtnAnimatedStyle}
            accessibilityLabel={prevBtnA11yLabel}
          />

          <HorizontalScrollRailNavBtn
            name="IconArrowRight"
            onPress={onNextBtnPress}
            disabled={disableNextBtn}
            style={nextBtnAnimatedStyle}
            accessibilityLabel={nextBtnA11yLabel}
          />
        </HStack>
      </FadeView>
    </View>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const HorizontalScrollRailNavBtn = (props: HorizontalScrollRailNavBtnProps) => {
  const { style, ...iconLinkProps } = props;

  return (
    <Animated.View style={style}>
      <IconLink
        color={theme.colors.BLACK}
        width={22}
        height={22}
        accessibilityRole="button"
        {...iconLinkProps}
      />
    </Animated.View>
  );
};

// ─── HOOKS ──────────────────────────────────────────────────────────────────────

const useNavBtnAnimatedStyle = (props: UseNavBtnAnimatedStyleProps) => {
  const { disabled } = props;

  const btnStateAnimation = useDerivedValue(() => {
    return withTiming(disabled ? 0 : 1, { duration: theme.transitions.base });
  }, [disabled]);

  return useAnimatedStyle(() => {
    return {
      opacity: interpolate(btnStateAnimation.value, [0, 1], [0.2, 1]),
    };
  }, []);
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type HorizontalScrollNavProps = {
  readonly count: number;
  readonly showNavControls?: boolean;
  readonly disablePrevBtn?: boolean;
  readonly disableNextBtn?: boolean;
  readonly onPrevBtnPress?: () => void;
  readonly onNextBtnPress?: () => void;
  readonly prevBtnA11yLabel?: string;
  readonly nextBtnA11yLabel?: string;
};

type HorizontalScrollRailNavBtnProps = ComponentProps<typeof IconLink> &
  Pick<ComponentProps<typeof Animated.View>, 'style'>;

type UseNavBtnAnimatedStyleProps = {
  readonly disabled: boolean;
};

const styles = StyleSheet.create({
  rowCenter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
});
